<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import KraftchatAIAPI from 'dashboard/api/kraftchatAI';

import KraftchatAIAgentForm from '../components/agent/KraftchatAIAgentForm.vue';
import KraftchatAIInboxList from '../components/agent/KraftchatAIInboxList.vue';
import KraftchatAIKnowledgeSection from '../components/knowledge/KraftchatAIKnowledgeSection.vue';
import KraftchatAIOverviewSection from '../components/overview/KraftchatAIOverviewSection.vue';
import KraftchatAIProviderForm from '../components/provider/KraftchatAIProviderForm.vue';
import KraftchatAIPlaygroundSection from '../components/playground/KraftchatAIPlaygroundSection.vue';
import KraftchatAIRulesSection from '../components/rules/KraftchatAIRulesSection.vue';
import { formatProviderName } from '../helpers/kraftchatAIFormatters';

const route = useRoute();
const router = useRouter();
const providerCredentials = ref([]);
const aiAgents = ref([]);
// Every AI-eligible inbox on the account (WhatsApp Cloud, the legacy
// Channel::Api WhatsApp bridge, Messenger, Instagram, Email, the Website
// Widget), whether or not it has an agent yet - see
// Api::V1::Accounts::KraftchatAi::AgentsController#eligible_inboxes.
const eligibleInboxes = ref([]);
const selectedInboxId = ref(null);
const isLoadingSettings = ref(false);
const settingsLoadError = ref('');

const selectedInbox = computed(() => {
  return (
    eligibleInboxes.value.find(
      inbox => String(inbox.id) === String(selectedInboxId.value)
    ) || null
  );
});

const selectedAgent = computed(() => {
  if (!selectedInbox.value?.agent_id) {
    return null;
  }

  return (
    aiAgents.value.find(agent => agent.id === selectedInbox.value.agent_id) ||
    null
  );
});

const handleSelectInbox = inbox => {
  selectedInboxId.value = inbox.id;
};

// The inbox list itself resolves whether an incomplete agent is missing
// rules specifically (vs. missing a provider credential, disabled, etc.) -
// see KraftchatAIInboxList's needsRulesOnly. Here we just act on that
// decision by sending the admin straight to "Regras e ações" with the
// right agent preselected, instead of a generic status badge with no path
// forward.
const handleConfigureRules = inbox => {
  router.push({
    name: 'kraftchat_ai_rules',
    params: route.params,
    query: { agent_id: inbox.agent_id },
  });
};

const pageContent = {
  kraftchat_ai_overview: {
    eyebrow: 'Kraftchat IA',
    title: 'Visão geral',
    description:
      'Acompanhe o status do assistente, canais preparados e próximos passos para ativar a IA no atendimento.',
    statusLabel: 'Status atual',
    statusValue: 'Em configuração',
    actionLabel: 'Configurar em breve',
    cards: [
      {
        icon: 'i-lucide-bot',
        title: 'Assistente IA',
        value: 'Rascunho',
        description: 'Estrutura inicial preparada para configurar a IA.',
      },
      {
        icon: 'i-lucide-message-circle',
        title: 'Canais',
        value: 'Website e WhatsApp',
        description: 'Canais previstos para atendimento automatizado.',
      },
      {
        icon: 'i-lucide-user-check',
        title: 'Atendimento humano',
        value: 'Obrigatório',
        description: 'Regras de transferência serão configuradas aqui.',
      },
    ],
  },
  kraftchat_ai_settings: {
    eyebrow: 'Kraftchat IA',
    title: 'Configuração',
    description:
      'Defina o tom de atendimento, dados da empresa e comportamento esperado do assistente.',
    statusLabel: 'Configuração',
    statusValue: 'Pendente',
    actionLabel: 'Salvar configuração em breve',
    cards: [
      {
        icon: 'i-lucide-sliders-horizontal',
        title: 'Tom de atendimento',
        value: 'Profissional',
        description: 'Ajuste o estilo de conversa da IA com os clientes.',
      },
      {
        icon: 'i-lucide-building-2',
        title: 'Dados da empresa',
        value: 'Pendente',
        description: 'Informe segmento, serviços e contexto do negócio.',
      },
      {
        icon: 'i-lucide-shield-check',
        title: 'Limites da IA',
        value: 'Em breve',
        description: 'Defina o que a IA pode ou não responder sozinha.',
      },
    ],
  },
  kraftchat_ai_knowledge: {
    eyebrow: 'Kraftchat IA',
    title: 'Base de conhecimento',
    description:
      'Organize conteúdos, perguntas frequentes e informações usadas pela IA para responder clientes.',
    statusLabel: 'Base',
    statusValue: 'Em breve',
    actionLabel: 'Adicionar conteúdo em breve',
    cards: [
      {
        icon: 'i-lucide-book-open',
        title: 'Artigos',
        value: 'Central de Ajuda',
        description: 'Use conteúdos existentes como base de resposta.',
      },
      {
        icon: 'i-lucide-list-checks',
        title: 'Perguntas frequentes',
        value: 'Pendente',
        description: 'Cadastre dúvidas comuns do negócio.',
      },
      {
        icon: 'i-lucide-file-text',
        title: 'Documentos',
        value: 'Em breve',
        description: 'Suporte para documentos será conectado depois.',
      },
    ],
  },
  kraftchat_ai_rules: {
    eyebrow: 'Kraftchat IA',
    title: 'Regras e ações',
    description:
      'Defina quando a IA deve responder, chamar humano e usar ferramentas do Kraftchat para adiantar o atendimento.',
    statusLabel: 'Regras',
    statusValue: 'Obrigatório',
    actionLabel: 'Configurar regras',
    cards: [
      {
        icon: 'i-lucide-alert-triangle',
        title: 'Casos sensíveis',
        value: 'Humano',
        description: 'Cobrança, cancelamento e reclamações devem escalar.',
      },
      {
        icon: 'i-lucide-clock',
        title: 'Horário de atendimento',
        value: 'Pendente',
        description: 'Defina como a IA atua fora do horário comercial.',
      },
      {
        icon: 'i-lucide-route',
        title: 'Transferência',
        value: 'Em breve',
        description: 'Direcione conversas para times e agentes corretos.',
      },
    ],
  },
  kraftchat_ai_playground: {
    eyebrow: 'Kraftchat IA',
    title: 'Testes',
    description:
      'Teste perguntas e simule respostas antes de ativar o assistente nos canais reais.',
    statusLabel: 'Ambiente',
    statusValue: 'Simulação',
    actionLabel: 'Testar em breve',
    cards: [
      {
        icon: 'i-lucide-message-square-text',
        title: 'Pergunta teste',
        value: 'Manual',
        description: 'Valide como a IA responderia uma dúvida comum.',
      },
      {
        icon: 'i-lucide-sparkles',
        title: 'Resposta da IA',
        value: 'Em breve',
        description: 'A resposta simulada aparecerá nesta área.',
      },
      {
        icon: 'i-lucide-clipboard-check',
        title: 'Validação',
        value: 'Pendente',
        description: 'Revise respostas antes de liberar em produção.',
      },
    ],
  },
};

const currentPage = computed(() => {
  return pageContent[route.name] || pageContent.kraftchat_ai_overview;
});

const isOverviewPage = computed(() => route.name === 'kraftchat_ai_overview');
const isSettingsPage = computed(() => route.name === 'kraftchat_ai_settings');
const isKnowledgePage = computed(() => route.name === 'kraftchat_ai_knowledge');
const isRulesPage = computed(() => route.name === 'kraftchat_ai_rules');
const isPlaygroundPage = computed(
  () => route.name === 'kraftchat_ai_playground'
);
const showSummaryCards = computed(() => {
  return !isOverviewPage.value && !isRulesPage.value && !isPlaygroundPage.value;
});

const configuredInboxesCount = computed(() => {
  return eligibleInboxes.value.filter(inbox => inbox.agent_id).length;
});

const settingsCards = computed(() => [
  {
    icon: 'i-lucide-key-round',
    title: 'Provedores',
    value: String(providerCredentials.value.length),
    description:
      providerCredentials.value.length > 0
        ? providerCredentials.value
            .map(provider => formatProviderName(provider.provider))
            .join(', ')
        : 'Nenhum provedor conectado ainda.',
  },
  {
    icon: 'i-lucide-bot',
    title: 'Agente selecionado',
    value: selectedAgent.value?.enabled ? 'Ativo' : 'Inativo',
    description: selectedAgent.value
      ? `${selectedAgent.value.name} em ${selectedInbox.value?.name}`
      : 'Escolha uma caixa de entrada para configurar um agente.',
  },
  {
    icon: 'i-lucide-message-circle',
    title: 'Caixas de entrada',
    value: String(configuredInboxesCount.value),
    description: eligibleInboxes.value.length
      ? `${configuredInboxesCount.value} de ${eligibleInboxes.value.length} caixa(s) com agente configurado.`
      : 'Nenhuma caixa de entrada disponível para atendimento com IA.',
  },
]);

const currentCards = computed(() => {
  if (isSettingsPage.value) {
    return settingsCards.value;
  }

  return currentPage.value.cards;
});

const loadKraftchatAISettings = async () => {
  if (!isSettingsPage.value && !isPlaygroundPage.value) {
    return;
  }

  isLoadingSettings.value = true;
  settingsLoadError.value = '';

  try {
    const [credentialsResponse, agentsResponse, eligibleInboxesResponse] =
      await Promise.all([
        KraftchatAIAPI.getProviderCredentials(),
        KraftchatAIAPI.getAgents(),
        KraftchatAIAPI.getEligibleInboxes(),
      ]);

    providerCredentials.value = credentialsResponse.data.payload || [];
    aiAgents.value = agentsResponse.data.payload || [];
    eligibleInboxes.value = eligibleInboxesResponse.data.payload || [];

    const stillExists = eligibleInboxes.value.some(
      inbox => String(inbox.id) === String(selectedInboxId.value)
    );
    if (!stillExists) {
      const preferredInbox =
        eligibleInboxes.value.find(inbox => inbox.agent_id) ||
        eligibleInboxes.value[0];
      selectedInboxId.value = preferredInbox?.id || null;
    }
  } catch (error) {
    settingsLoadError.value =
      error.response?.data?.message ||
      error.message ||
      'Não foi possível carregar a configuração da IA.';
  } finally {
    isLoadingSettings.value = false;
  }
};

onMounted(loadKraftchatAISettings);

watch(
  () => route.name,
  () => {
    loadKraftchatAISettings();
  }
);
</script>

<template>
  <main class="flex-1 h-full overflow-y-auto bg-n-background">
    <section class="max-w-7xl mx-auto px-6 py-8 space-y-6">
      <header class="space-y-2">
        <p class="text-sm font-medium text-n-brand">
          {{ currentPage.eyebrow }}
        </p>

        <h1 class="text-2xl font-semibold text-n-slate-12">
          {{ currentPage.title }}
        </h1>

        <p class="max-w-3xl text-sm leading-6 text-n-slate-11">
          {{ currentPage.description }}
        </p>
      </header>

      <section
        v-if="showSummaryCards"
        class="grid gap-4 md:grid-cols-2 xl:grid-cols-3"
      >
        <article
          v-for="card in currentCards"
          :key="card.title"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-5 shadow-sm transition-colors hover:border-n-strong"
        >
          <div class="flex items-start gap-3">
            <div
              class="grid size-9 flex-shrink-0 place-content-center rounded-lg bg-n-alpha-1 text-n-brand"
            >
              <span class="size-5" :class="[card.icon]" />
            </div>

            <div class="min-w-0 space-y-2">
              <div class="flex flex-wrap items-center gap-2">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{ card.title }}
                </h3>

                <span
                  class="rounded-md bg-n-brand/10 px-2 py-0.5 text-xs font-medium text-n-brand"
                >
                  {{ card.value }}
                </span>
              </div>

              <p class="text-sm leading-6 text-n-slate-11">
                {{ card.description }}
              </p>
            </div>
          </div>
        </article>
      </section>

      <KraftchatAIOverviewSection v-if="isOverviewPage" />

      <KraftchatAIProviderForm
        v-if="isSettingsPage"
        :provider-credentials="providerCredentials"
        @saved="loadKraftchatAISettings"
      />

      <KraftchatAIKnowledgeSection v-if="isKnowledgePage" />

      <KraftchatAIRulesSection v-if="isRulesPage" />

      <KraftchatAIPlaygroundSection
        v-if="isPlaygroundPage"
        :agents="aiAgents"
      />

      <template v-if="isSettingsPage">
        <KraftchatAIInboxList
          :inboxes="eligibleInboxes"
          :selected-inbox-id="selectedInboxId"
          @select="handleSelectInbox"
          @configure-rules="handleConfigureRules"
        />

        <KraftchatAIAgentForm
          v-if="selectedInbox"
          :agent="selectedAgent"
          :inbox="selectedInbox"
          :provider-credentials="providerCredentials"
          @saved="loadKraftchatAISettings"
        />
      </template>
    </section>
  </main>
</template>
